import React from "react";
import { Box, Button, Flex, Image, Text } from "@chakra-ui/react";
import type { BoxProps, ButtonProps, FlexProps, ImageProps, TextProps } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { AuthService } from "services/authService";
import { adminUrl } from "services/config";
import noFollowupsImage from "assets/images/no_followups.png";
import logoutForbiddenImage from "assets/images/logout-forbidden.png";
import WhiteLogo from "assets/images/Myran.svg";

const pageWrapperStyles: FlexProps = {
  minH: "100vh",
  bg: "gray.50",
  direction: "column",
};

const headerBoxStyles: BoxProps = {
  w: "full",
  bgGradient: "linear(to-r, #263CCA, #6C39F6)",
  px: { base: 4, md: 12 },
  h: "60px",
  display: "flex",
  alignItems: "center",
  boxShadow: "md",
};

const logoStyles: ImageProps = {
  src: WhiteLogo,
  alt: "Myran",
  w: "60px",
  h: "20px",
};

const mainWrapperStyles: FlexProps = {
  flex: 1,
  w: "full",
  justify: "center",
  align: "center",
  px: { base: 4, md: 12 },
  py: { base: 8, md: 12 },
};

const contentColumnStyles: FlexProps = {
  direction: "column",
  w: "full",
  maxW: "850px",
  align: "center",
};

const cardBoxStyles: BoxProps = {
  w: "full",
  bg: "white",
  border: "1px solid",
  borderColor: "mainBlue.50",
  borderRadius: "24px",
  boxShadow: "sm",
  textAlign: "center",
  px: { base: 6, md: 12 },
  py: { base: 10, md: 16 },
};

const cardContentStyles: FlexProps = {
  direction: "column",
  align: "center",
  gap: 4,
};

const noFollowupsImageStyles: ImageProps = {
  src: noFollowupsImage,
  alt: "No follow-ups",
  width: "60px",
  height: "64px",
};

const headingTextStyles: TextProps = {
  fontFamily: "Open Sans",
  fontWeight: "700",
  fontStyle: "normal",
  fontSize: "18px",
  lineHeight: "20px",
  letterSpacing: "-0.02em",
  textAlign: "center",
  color: "mainBlue.900",
};

const descriptionTextStyles: TextProps = {
  fontFamily: "Open Sans",
  fontWeight: "400",
  fontSize: "14px",
  lineHeight: "22px",
  letterSpacing: "0",
  textAlign: "center",
  color: "mainBlue.700",
  maxW: "100%",
};

const createFollowupsButtonStyles: ButtonProps = {
  variant: "outline",
  color: "#3443D5",
  bgColor: "white",
  fontWeight: "600",
  borderColor: "#3443D5",
  border: "2px solid",
  px: "10",
  h: "34px",
  _hover: { opacity: 0.7 },
  fontFamily: "Open Sans",
  fontSize: "13px",
  lineHeight: "100%",
  letterSpacing: "0",
  textAlign: "center",
  mt: 4,
};

const logoutButtonStyles: ButtonProps = {
  mt: 10,
  variant: "link",
  color: "mainBlue.500",
  alignSelf: "flex-start",
};

const logoutIconStyles: ImageProps = {
  src: logoutForbiddenImage,
  alt: "Logout",
  boxSize: "24px",
};

export default function FollowUpForbidden() {
  const { t } = useTranslation();

  const goToCreateFollowUps = () => {
    if (adminUrl) {
      window.open(adminUrl, "_blank", "noopener");
    }
  };

  const handleLogout = () => {
    AuthService.logout();
  };

  return (
    <Flex {...pageWrapperStyles}>
      <Box {...headerBoxStyles}>
        <Image {...logoStyles} />
      </Box>
      <Flex {...mainWrapperStyles}>
        <Flex {...contentColumnStyles}>
          <Box {...cardBoxStyles}>
            <Flex {...cardContentStyles}>
              <Image {...noFollowupsImageStyles} />
              <Text {...headingTextStyles}>{t("No_Available_FollowUp")}</Text>
              <Text {...descriptionTextStyles}>{t("No_FollowUp_Assigned_Text")}</Text>
              <Button onClick={goToCreateFollowUps} {...createFollowupsButtonStyles}>
                {t("Create_Followups_Button")}
              </Button>
            </Flex>
          </Box>
          <Button {...logoutButtonStyles} leftIcon={<Image {...logoutIconStyles} />} onClick={handleLogout}>
            {t("MainHeader_Logout")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
